import React, { Fragment, useMemo } from "react";

import type {
  ProcessedBetEntry,
  ProcessedSelection,
} from "@/hooks/firestore/betting/useEntries";

import cx from "classnames";
import { isOutright } from "@/utilities/sharedBettingUtilities";
import { hasTrackableBets } from "@/features/entries/util/bet-tracker-helpers";
import { useDockActions } from "@/features/shell/hooks/useDock";

import { isQuaddieSelection, isSingleSelection } from "../../util/type-guards";
import {
  isEntryCashedOut,
  isEntryDecided,
  mysteryMultiOf,
} from "../../util/entry-helpers";
import { MultiLegStatus } from "../bet-tracker/LegStatus";
import { BetTrackerLogo } from "../bet-tracker/BetTrackerLogo";
import { QuaddieSelection } from "../selections/QuaddieSelection";
import { ShareAddToBetslip } from "../share/ShareAddToBetslip";
import { ShareQuaddie } from "../share/ShareQuaddie";
import { BetEntrySelection } from "./BetEntrySelection";

import * as styles from "./BetEntrySelectionList.module.scss";

interface BetEntrySelectionListProps {
  entry: ProcessedBetEntry;
  isPreview?: boolean;
}

const BetEntrySelectionList = ({
  entry,
  isPreview,
}: BetEntrySelectionListProps) => {
  const isEntryWon = entry.data.result === "WIN";
  const isDecided = isEntryDecided(entry);
  const isCashedOut = isEntryCashedOut(entry);
  const { openDock } = useDockActions();

  const showBetTrackerLogo = hasTrackableBets(entry);
  const mysteryMulti = mysteryMultiOf(
    entry.data.multiBuilderType,
    entry.data.mysteryMultiLevel,
  );

  // Quaddies are a special case as they are displayed
  // like one selection with multiple outcomes, but
  // they are actually multiple selections.
  if (isQuaddieSelection(entry.data.selections[0])) {
    return (
      <>
        <QuaddieSelection entry={entry} isPreview={isPreview} />
        {entry.shareId &&
          entry.data.quaddieMarketId &&
          entry.data.quaddieMarket && (
            <ShareQuaddie
              selections={entry.data.selections}
              quaddieId={entry.data.quaddieMarketId}
              quaddieType={entry.data.quaddieMarket.type}
            />
          )}
      </>
    );
  }

  const sortedSelections = useMemo(() => {
    return [...entry.data.selections].sort((a, b) => {
      if (a.result === "WIN" && b.result !== "WIN") return -1;
      if (a.result !== "WIN" && b.result === "WIN") return 1;

      const isAOutright = isOutright(a.event.eventType);
      const isBOutright = isOutright(b.event.eventType);

      // if selection is futures, use nextBetStop as the start time
      const aStartTime =
        isAOutright && a.marketNextBetStop
          ? a.marketNextBetStop
          : a.event.scheduledStartTime ?? "";

      const bStartTime =
        isBOutright && b.marketNextBetStop
          ? b.marketNextBetStop
          : b.event.scheduledStartTime ?? "";

      return new Date(aStartTime).getTime() - new Date(bStartTime).getTime();
    });
  }, [entry.data.selections]);

  const isCombo = !!entry.data.selectionsRequiredToWin;
  // legs join when both landed (won/void). Cashout draws its own blue line.
  // Skip combos because the order doesn't matter, we don't need to join them unless they all win
  const canBeConnected = !isCombo && !isCashedOut;

  const winOrVoid = (selection: ProcessedSelection) =>
    selection.result === "WIN" || selection.result === "VOID";

  return (
    <>
      {sortedSelections.length > 1 && (
        <div
          className={cx(styles.multiLegStatusContainer, {
            [styles.hasTrackableBets]: showBetTrackerLogo,
            [styles.isWon]: isEntryWon,
            [styles.isCombo]: !!entry.data.selectionsRequiredToWin,
          })}
        >
          {showBetTrackerLogo && <BetTrackerLogo />}
          <MultiLegStatus
            isEntryWon={isEntryWon}
            selections={sortedSelections}
          />
        </div>
      )}
      {sortedSelections.map((selection, index) => {
        const isLast = index === sortedSelections.length - 1;
        const shouldConnect = canBeConnected && winOrVoid(selection);
        const connectUp =
          shouldConnect && index > 0 && winOrVoid(sortedSelections[index - 1]);
        const connectDown =
          shouldConnect &&
          (isLast ? isEntryWon : winOrVoid(sortedSelections[index + 1]));

        return (
          <Fragment key={getSelectionId(entry.data.id, selection)}>
            <BetEntrySelection
              entryId={entry.entryId}
              isShared={!!entry.shareId}
              isCombo={isCombo}
              selection={selection}
              isFirst={index === 0}
              isLast={isLast}
              isCashedOut={isCashedOut}
              isEntryWon={isEntryWon}
              isDecided={isDecided}
              isPreview={isPreview}
              hasMultipleSelections={entry.data.selections.length > 1}
              connectUp={connectUp}
              connectDown={connectDown}
              createdAt={entry.createdAt}
            />
            {!!entry.shareId && (
              <ShareAddToBetslip
                onPickAddedToBetslip={() => openDock("betslip")}
                selection={selection}
                mysteryMulti={mysteryMulti}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

const getSelectionId = (entryId: string, selection: ProcessedSelection) => {
  if (isSingleSelection(selection)) {
    return `${entryId}-${selection.outcomeId}`;
  }

  const outcomeIds = Object.values(selection.outcomes).map(
    (outcome) => outcome.outcomeId,
  );

  return `${entryId}-${outcomeIds.join("-")}`;
};

export { BetEntrySelectionList };
